import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CommonTable from "../../../../common/table/ComonTable";
import DeleteModal from "../../../../common/commonmodal/DeleteModal";
import CommonModal from "../../../../common/commonmodal/CommonModal";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faUser, faEye, faFileAlt, faExternalLinkAlt, faArrowLeft, faTrash } from "@fortawesome/free-solid-svg-icons";
import apiClient from "../../../../../services/apiClient";
import storageService from "../../../../../utils/storageService";

const VendorStaffList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector((state) => state.auth);

    const searchParams = new URLSearchParams(location.search);
    const vendorId = searchParams.get("vendorId");
    const vendorName = searchParams.get("vendorName") || "Vendor";

    const assignedSiteId = user?.siteId || user?.SiteId || Number(storageService.getItem("siteId")) || 2;

    const [staffList, setStaffList] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState("");
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0
    });

    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        staffId: null,
        staffName: ""
    });

    const [previewDoc, setPreviewDoc] = useState({
        isOpen: false,
        doc: null,
        staffName: ""
    });

    const fetchStaff = useCallback(async (customParams = {}) => {
        setLoading(true);
        try {
            const params = {
                siteId: assignedSiteId,
                ...(vendorId ? { vendorId: Number(vendorId) } : {}),
                ...(selectedRole ? { staffRoleId: Number(selectedRole) } : {}),
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
                ...customParams
            };

            const cleanParams = Object.fromEntries(
                Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== "")
            );

            const response = await apiClient.get('/staff-management/staff/GetStaff', {
                params: cleanParams
            });

            if (response.data?.isSuccess && response.data?.data) {
                const list = response.data.data.map(item => {
                    const primaryDoc = Array.isArray(item.documents) && item.documents.length > 0 ? item.documents[0] : null;
                    const primaryAllotment = Array.isArray(item.allotments) && item.allotments.length > 0 ? item.allotments[0] : null;
                    
                    return {
                        ...item,
                        id: item.staffId,
                        fullName: `${item.firstName || ''} ${item.lastName || ''}`.trim() || "—",
                        document: primaryDoc,
                        allotment: primaryAllotment
                    };
                });
                
                setStaffList(list);
                setPagination(prev => ({
                    ...prev,
                    totalRecords: response.data.totalCount || list.length
                }));
            } else {
                setStaffList([]);
                setPagination(prev => ({ ...prev, totalRecords: 0 }));
            }
        } catch (error) {
            console.error("Failed to fetch vendor staff list:", error);
            toast.error("Failed to load vendor staff list");
            setStaffList([]);
        } finally {
            setLoading(false);
        }
    }, [assignedSiteId, vendorId, selectedRole, pagination.pageNumber, pagination.pageSize]);

    const fetchRoles = useCallback(async () => {
        try {
            const res = await apiClient.get('/staff-management/staff/GetRoles', {
                params: { siteId: assignedSiteId }
            });
            if (res.data?.isSuccess && res.data?.data) {
                setRoles(res.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch roles", error);
        }
    }, [assignedSiteId]);

    useEffect(() => {
        fetchStaff();
    }, [fetchStaff]);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    const tableHeaders = [
        { label: "STAFF CODE", key: "generatedUniqueId", align: "center" },
        { label: "STAFF NAME", key: "fullName", align: "left" },
        { label: "PHONE", key: "mobileNumber", align: "center" },
        { label: "ROLE", key: "roleName", align: "left" },
        { label: "DUTY", key: "shift", align: "left" },
        { label: "DOCUMENT", key: "document", align: "center" },
        { label: "STATUS", key: "employmentStatus", align: "center" },
        { label: "ACTIONS", key: "actions", align: "center" }
    ];

    const renderTableCell = (key, row) => {
        if (key === "generatedUniqueId") {
            return (
                <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">
                    {row.generatedUniqueId || "—"}
                </span>
            );
        }

        if (key === "fullName") {
            return (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center shadow-sm shrink-0">
                        {row.photoUrl ? (
                            <img
                                src={row.photoUrl}
                                alt={row.fullName}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                }}
                            />
                        ) : (
                            <FontAwesomeIcon icon={faUser} className="text-gray-400 text-xs" />
                        )}
                    </div>
                    <div className="flex flex-col text-left">
                        <span className="text-[13px] font-semibold text-gray-800 dark:text-textprimary tracking-tight">{row.fullName}</span>
                        <span className="text-[10px] text-gray-400 dark:text-textsecondary font-normal">ID : {row.staffId}</span>
                    </div>
                </div>
            );
        }

        if (key === "mobileNumber") {
            return <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">{row.mobileNumber || "—"}</span>;
        }

        if (key === "roleName") {
            const parts = (row.roleName || "—").split(" ");
            return (
                <div className="flex flex-col text-left">
                    <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">{parts[0]}</span>
                    {parts.length > 1 && (
                        <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">{parts.slice(1).join(" ")}</span>
                    )}
                </div>
            );
        }

        if (key === "shift") {
            const startDate = row.allotment?.startDate ? new Date(row.allotment.startDate).toLocaleDateString('en-GB') : "";
            const endDate = row.allotment?.endDate ? new Date(row.allotment.endDate).toLocaleDateString('en-GB') : "";
            const dateStr = startDate && endDate ? `${startDate} to ${endDate}` : "";

            return (
                <div className="flex flex-col text-left">
                    <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">
                        {row.allotment?.unitNumber ? `${row.allotment.unitNumber} - ${row.allotment.unitName || ''}` : "—"}
                    </span>
                    {dateStr && (
                        <span className="text-[10px] text-gray-400 dark:text-textsecondary font-normal mt-0.5">
                            {dateStr}
                        </span>
                    )}
                </div>
            );
        }

        if (key === "document") {
            if (row.document?.documentUrl) {
                return (
                    <button
                        type="button"
                        onClick={() => setPreviewDoc({
                            isOpen: true,
                            doc: row.document,
                            staffName: row.fullName
                        })}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#EFF6FF] hover:bg-blue-100 dark:bg-blue-950/40 dark:hover:bg-blue-900/50 border border-[#DBEAFE] dark:border-blue-900/50 text-[#2563EB] dark:text-blue-400 rounded-xl text-xs font-semibold shadow-2xs transition-all duration-150 cursor-pointer"
                        title="Click to preview document"
                    >
                        <FontAwesomeIcon icon={faFileAlt} className="text-[#2563EB] dark:text-blue-400 text-xs" />
                        <span>{row.document.documentType || "Document"}</span>
                        <FontAwesomeIcon icon={faEye} className="text-[#3B82F6] dark:text-blue-400 text-[11px] ml-0.5" />
                    </button>
                );
            }
            return <span className="text-[13px] font-normal text-gray-400 dark:text-textsecondary">—</span>;
        }

        if (key === "employmentStatus") {
            const isActive = (row.employmentStatus || "").toLowerCase() === "active" || row.isActive;
            return (
                <span className={`px-3 py-1 text-xs font-bold rounded-lg ${isActive
                    ? "bg-[#DCFCE7] text-[#166534]"
                    : "bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-300"
                    }`}>
                    {isActive ? "Active" : "Inactive"}
                </span>
            );
        }

        return <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">{row[key] || "—"}</span>;
    };

    const onPageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, pageNumber: newPage }));
    };

    const handleLimitChange = (newLimit) => {
        setPagination((prev) => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
    };

    const handleDeleteStaff = async () => {
        if (!deleteModal.staffId) return;
        try {
            toast.success(`Staff ${deleteModal.staffName} removed successfully`);
            setDeleteModal({ isOpen: false, staffId: null, staffName: "" });
            fetchStaff();
        } catch (error) {
            console.error("Failed to delete staff:", error);
            toast.error("Failed to delete staff");
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <div className="flex items-center justify-between mb-1">
                <Breadcrumbs items={["Workforce Management", "Vendor", `${vendorName} Staff`]} />
                <button
                    onClick={() => navigate("/zentcomm/workforce-management/vendor")}
                    className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-textprimary flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="text-[10px]" />
                    Back to Vendors
                </button>
            </div>

            <PageHeadingSection
                Navbar={true}
                mainHeading={`${vendorName}`}
                subHeading={`Manage staff members, roles, and status for ${vendorName}.`}
                primaryBtnText="Add Staff"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() =>
                    navigate(
                        `/zentcomm/workforce-management/vendor/staff/add?vendorId=${vendorId || ""}&vendorName=${encodeURIComponent(
                            vendorName
                        )}`
                    )
                }
            />

            <div className="shrink-0 flex-1 flex flex-col mt-6">
                <div className="flex justify-end mb-4">
                    <select
                        className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-cardbg text-gray-700 dark:text-textprimary text-xs rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-blue-500 w-[220px]"
                        value={selectedRole}
                        onChange={(e) => {
                            setSelectedRole(e.target.value);
                            setPagination(prev => ({ ...prev, pageNumber: 1 }));
                        }}
                    >
                        <option value="">All Roles</option>
                        {roles.map((r) => (
                            <option key={r.roleId} value={r.roleId}>
                                {r.roleName}
                            </option>
                        ))}
                    </select>
                </div>
                <CommonTable
                    loading={loading}
                    tableHeading="Staff List"
                    headers={tableHeaders}
                    data={staffList}
                    renderCell={renderTableCell}
                    pagination={{
                        pageNumber: pagination.pageNumber,
                        pageSize: pagination.pageSize,
                        totalRecords: pagination.totalRecords,
                        totalPages: Math.ceil(pagination.totalRecords / pagination.pageSize) || 1
                    }}
                    customAction={{
                        label: "Manage Staff",
                        icon: faEye,
                        onClick: (row) =>
                            navigate(
                                `/zentcomm/workforce-management/vendor/staff/add?vendorId=${vendorId || ""}&vendorName=${encodeURIComponent(
                                    vendorName
                                )}`,
                                { state: { staffData: row } }
                            )
                    }}
                    onDelete={(row) => setDeleteModal({ isOpen: true, staffId: row.staffId || row.id, staffName: row.fullName })}
                    onPageChange={onPageChange}
                    onLimitChange={handleLimitChange}
                    isLoading={loading}
                />
            </div>

            {/* Document Preview Modal */}
            <CommonModal
                isOpen={previewDoc.isOpen}
                onClose={() => setPreviewDoc({ isOpen: false, doc: null, staffName: "" })}
                title={`Document Preview - ${previewDoc.doc?.documentType || "Identity Document"}`}
                subTitle={previewDoc.staffName ? `Staff Member: ${previewDoc.staffName}` : "Uploaded Document Preview"}
                footerContent={
                    <div className="flex items-center justify-between w-full">
                        <div className="text-xs text-[#64748B] dark:text-textsecondary">
                            Document Type: <span className="font-semibold text-gray-800 dark:text-textprimary">{previewDoc.doc?.documentType || "—"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {previewDoc.doc?.documentUrl && (
                                <a
                                    href={previewDoc.doc.documentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[11px]" />
                                    Open Original
                                </a>
                            )}
                            <button
                                type="button"
                                onClick={() => setPreviewDoc({ isOpen: false, doc: null, staffName: "" })}
                                className="px-4 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-cardbg dark:hover:bg-actionitembg text-gray-700 dark:text-textprimary rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                }
            >
                <div className="flex flex-col gap-4">
                    {/* Document Meta Information Header */}
                    <div className="grid grid-cols-2 gap-3 p-3.5 bg-gray-50 dark:bg-dashboardbg rounded-lg border border-gray-200/80 dark:border-bordercolor">
                        <div>
                            <span className="block text-[11px] text-gray-500 dark:text-textsecondary uppercase tracking-wider font-medium">Document Type</span>
                            <span className="text-xs font-semibold text-gray-800 dark:text-textprimary mt-0.5 block">
                                {previewDoc.doc?.documentType || "—"}
                            </span>
                        </div>
                        <div>
                            <span className="block text-[11px] text-gray-500 dark:text-textsecondary uppercase tracking-wider font-medium">Document Number</span>
                            <span className="text-xs font-semibold text-gray-800 dark:text-textprimary mt-0.5 block">
                                {previewDoc.doc?.documentNumber || "—"}
                            </span>
                        </div>
                    </div>

                    {/* Document Display / Preview */}
                    <div className="flex items-center justify-center p-2 bg-gray-100/60 dark:bg-cardbg rounded-lg min-h-[250px] max-h-[60vh] overflow-auto border border-gray-200 dark:border-bordercolor">
                        {previewDoc.doc?.documentUrl ? (
                            previewDoc.doc.documentUrl.toLowerCase().endsWith(".pdf") ? (
                                <iframe
                                    src={previewDoc.doc.documentUrl}
                                    title="Document Preview PDF"
                                    className="w-full h-[50vh] rounded border-0"
                                />
                            ) : (
                                <img
                                    src={previewDoc.doc.documentUrl}
                                    alt={previewDoc.doc?.documentType || "Document"}
                                    className="max-h-[50vh] max-w-full object-contain rounded-md shadow-sm"
                                    onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        e.currentTarget.nextSibling.style.display = "flex";
                                    }}
                                />
                            )
                        ) : (
                            <div className="text-center py-10 text-gray-400 text-xs">
                                No document file available for preview
                            </div>
                        )}
                        <div className="hidden flex-col items-center justify-center py-10 text-gray-400 text-xs gap-2">
                            <span>Could not load document preview inline.</span>
                            {previewDoc.doc?.documentUrl && (
                                <a
                                    href={previewDoc.doc.documentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline font-medium"
                                >
                                    Click here to open directly
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </CommonModal>

            <DeleteModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, staffId: null, staffName: "" })}
                onConfirm={handleDeleteStaff}
                title="Remove Staff"
                message={`Are you sure you want to remove "${deleteModal.staffName}" from ${vendorName}?`}
            />
        </div>
    );
};

export default VendorStaffList;

